import React, { useEffect, useRef, useState } from 'react';
import { fetchAdmins, createAdmin, deleteAdmin } from '../../utils/accountsApi';
import { getCurrentUserEmail } from '../../utils/currentUser';
import './Classes.css';
import './Teachers.css';
import Footer from '../Footer';

const MAX_ADMIN_ACCOUNTS = 8;

function CreateAdminRow({ onCancel, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    if (!trimmedFirstName && !trimmedLastName && !trimmedEmail) { onCancel(); return; }
    if (!trimmedFirstName) { setError('First name is required.'); return; }
    if (!trimmedLastName) { setError('Last name is required.'); return; }
    if (!trimmedEmail) { setError('Email is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await createAdmin(trimmedFirstName, trimmedLastName, trimmedEmail);
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not create the admin account. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="class-card class-card-creating">
      <div className="class-card-main">
        <div className="teacher-create-fields">
          <input
            ref={inputRef}
            className="review-name-input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            disabled={saving}
            required
          />
          <input
            className="review-name-input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            disabled={saving}
            required
          />
          <input
            type="email"
            className="review-name-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            disabled={saving}
            required
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel(); }}
          />
        </div>
        {error && <span className="review-field-error">{error}</span>}
      </div>
      <div className="class-card-actions">
        <button className="title-button" onClick={commit} disabled={saving}>
          {saving ? 'Creating...' : 'Create'}
        </button>
        <button className="upload-cancel-btn" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function AdminRow({ admin, isSelf, isLastAdmin, onDeleted }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAdmin(admin.id);
      onDeleted(admin.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete this admin account. Please try again.');
      setDeleting(false);
    }
  };

  const deleteDisabledReason = isSelf
    ? "You can't delete your own admin account."
    : isLastAdmin
      ? 'At least one admin account must remain.'
      : undefined;

  return (
    <div className="class-card">
      <div className="class-card-main">
        <div className="class-name-row">
          <span className="class-name">{admin.name}</span>
          {isSelf && <span className="class-detail-count">You</span>}
        </div>
        <span className="class-meta-item">{admin.email}</span>
      </div>
      <div className="class-card-actions">
        {confirmDelete ? (
          <div className="delete-confirm">
            <span className="delete-confirm-text">Delete this account?</span>
            <button className="delete-confirm-yes" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button className="delete-confirm-no" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
          </div>
        ) : (
          <button
            className="class-remove-btn"
            onClick={() => setConfirmDelete(true)}
            disabled={isSelf || isLastAdmin}
            title={deleteDisabledReason || 'Delete account'}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

function Admins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const currentUserEmail = getCurrentUserEmail();

  useEffect(() => {
    fetchAdmins()
      .then((res) => { if (Array.isArray(res.data)) setAdmins(res.data); })
      .catch(() => setAdmins([]))
      .finally(() => setLoading(false));
  }, []);

  const handleCreated = (newAdmin) => {
    setAdmins((prev) => [...prev, newAdmin]);
    setCreating(false);
  };

  const handleDeleted = (id) => {
    setAdmins((prev) => prev.filter((a) => a.id !== id));
  };

  const realAdmins = admins.filter((a) => a.tag !== 'demo');
  const demoAdmins = admins.filter((a) => a.tag === 'demo');
  const atCapacity = realAdmins.length >= MAX_ADMIN_ACCOUNTS;

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <div className="title-name-container">
            <header className="name-header">Admins</header>
          </div>
        </div>
        <div className="reload-button-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!loading && (
            <span className="account-count-indicator">
              {realAdmins.length}/{MAX_ADMIN_ACCOUNTS} accounts
            </span>
          )}
          <button
            className="reload-button"
            onClick={() => setCreating(true)}
            disabled={creating || atCapacity}
            title={atCapacity ? `Maximum of ${MAX_ADMIN_ACCOUNTS} admin accounts reached` : undefined}
          >
            + Add Admin
          </button>
        </div>
      </div>

      <div className="component-container">
        <div className="classes-content">
          {!loading && atCapacity && (
            <p className="chat-card-hint">
              Maximum of {MAX_ADMIN_ACCOUNTS} admin accounts reached — delete one to add another.
            </p>
          )}
          {loading ? (
            <div className="cases-loading">Loading admins...</div>
          ) : (
            <>
              <div className="classes-list">
                {creating && !atCapacity && (
                  <CreateAdminRow onCancel={() => setCreating(false)} onCreated={handleCreated} />
                )}
                {realAdmins.length === 0 && !creating && (
                  <p className="chat-card-hint">No admin accounts yet — click "+ Add Admin" to create one.</p>
                )}
                {realAdmins.map((admin) => (
                  <AdminRow
                    key={admin.id}
                    admin={admin}
                    isSelf={admin.email === currentUserEmail}
                    isLastAdmin={admins.length <= 1}
                    onDeleted={handleDeleted}
                  />
                ))}
              </div>

              {demoAdmins.length > 0 && (
                <>
                  <h2 className="cases-section-heading">
                    Demo Accounts <span className="cases-section-hint">{demoAdmins.length} accounts</span>
                  </h2>
                  <div className="classes-list">
                    {demoAdmins.map((admin) => (
                      <AdminRow
                        key={admin.id}
                        admin={admin}
                        isSelf={admin.email === currentUserEmail}
                        isLastAdmin={admins.length <= 1}
                        onDeleted={handleDeleted}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Admins;
